import { Component, PLATFORM_ID, inject, input, OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { LoaderServices } from '../../../shared/services/loader.service';
import { UserApiService } from '../../../shared/services/user-api.service';
import { SubscriptionService } from '../../../shared/services/subscription.service';
import { AnalyticsUtils } from '../../../shared/services/analytics-utils';
import { SharedService } from '../../../shared/services/shared.service';
import { ReferrerTrackingService } from '../../../shared/services/referrer-tracking.service';
import { ReturnUrlService } from '../../../shared/services/return-url.service';
import firebase from 'firebase/compat/app';

@Component({
   selector: 'app-google-login',
   templateUrl: './google-login.component.html',
   styleUrls: ['./google-login.component.scss'],
   host: {
      '[class.btn-styling]': 'isGoogleBtnInsidePopup()'
   }
})
export class GoogleLoginComponent implements OnInit {
   // Inputs using signal-based input() API
   buttonText = input<string>('Login via Google');
   clickEvent = input<string>('');
   tapEvent = input<string>('');
   completedEvent = input<string>('');
   failEvent = input<string>('');
   isGoogleBtnInsidePopup = input<boolean>(false);

   // Depedency Injections
   private readonly platformId = inject(PLATFORM_ID);
   private readonly authFire = inject(AngularFireAuth);
   private readonly router = inject(Router);
   private readonly auth = inject(AuthService);
   private readonly loader = inject(LoaderServices);
   private readonly userApiService = inject(UserApiService);
   private readonly subscriptionService = inject(SubscriptionService);
   private readonly analyticsUtils = inject(AnalyticsUtils);
   private readonly sharedService = inject(SharedService);
   private readonly referrerTrackingService = inject(ReferrerTrackingService);
   private readonly returnUrlService = inject(ReturnUrlService);

   private readonly isBrowser = isPlatformBrowser(this.platformId);
   private token: any = null;
   private redirectURL: string | null = null;
   public message: unknown;

   constructor() {
      if (this.isBrowser) {
         this.redirectURL = localStorage.getItem('redirectUrl');
      }
   }

   ngOnInit(): void {
      if (this.isBrowser) {
         this.returnUrlService.captureReturnUrl();
      }
   }

   async handleClick() {
      if (!this.isBrowser) {
         console.warn('Google Sign-in is not available in SSR context');
         return;
      }

      try {
         this.analyticsUtils.SOCIAL_AUTH_BTN_TAP(this.tapEvent());
         const result = await this.authFire.signInWithPopup(
            new firebase.auth.GoogleAuthProvider()
         );
         await this.handleAuthSuccess(result);
      } catch (error: unknown) {
         this.handleAuthError(error);
      }
   }

   private async handleAuthSuccess(value: any) {
      try {
         const accessToken = value.credential.accessToken;
         this.loader.show();
         const response: any = await this.userApiService.googleLogin({ token: accessToken });

         if (response.status) {
            await this.processSuccessfulLogin(response);
            this.referrerTrackingService.clearReferrerData();
         } else {
            this.handleLoginFailure(response);
         }
      } finally {
         this.loader.hide();
      }
   }

   private async processSuccessfulLogin(response: any) {
      this.token = response.data;
      const isNew = response.is_new;

      if (this.isGoogleBtnInsidePopup()) {
         this.auth.closeAuthPopup();
      }

      await this.auth.setToken(this.token);
      const userData: any = await this.userApiService.fetchUserDetailsAndSave();
      this.auth.setLoggedIn();

      if (!isNew) {
         await this.subscriptionService.setUserSubscriptionInfo();
      }
      await this.handleSubscriptionStatus(isNew, userData);
   }

   private async handleSubscriptionStatus(isNew: boolean, userData: any) {
      const subInfo = SubscriptionService.subscriptionData.getValue();
      if (isNew) {
         this.sharedService.afterSignUpActions(userData.email);
         this.analyticsUtils.SignupFlow_SaveUser_Completed('google');
      }

      this.analyticsUtils.SOCIAL_AUTH_COMPLETED(
         this.completedEvent(),
         userData.email,
         isNew,
         AnalyticsUtils.METHOD_GOOGLE
      );

      if (!userData?.sign_up_completed) {
         if (this.isGoogleBtnInsidePopup()) {
            this.auth.closeAuthPopup();
         }
         this.router.navigate(['/authentication/activate']);
         return;
      }

      if (this.isGoogleBtnInsidePopup()) {
         window.location.reload();
      } else {
         const returnUrl = this.returnUrlService.getReturnUrl();
         if (returnUrl) {
            this.returnUrlService.redirectAfterAuth('/', this.token || '');
         } else if (this.redirectURL) {
            this.router.navigateByUrl(this.redirectURL);
         } else {
            this.router.navigateByUrl('/');
         }
      }
   }

   private handleAuthError(error: any) {
      this.loader.hide();
      this.analyticsUtils.SOCIAL_AUTH_FAILED(this.failEvent(), '', '', error?.message || 'Unknown error');
      console.error('Google sign-in error:', error);
   }

   private handleLoginFailure(response: any) {
      this.message = response.message;
      this.analyticsUtils.SOCIAL_AUTH_FAILED(this.failEvent(), '', response.message, null);
   }

   async logout() {
      this.auth.logout();
      this.subscriptionService.clearSubscriptionInfo();
   }
}
